import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import plusIcon from '../../assets/icons/plus.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import checkIcon from '../../assets/icons/check.svg';
import { useAuth } from '../../context/AuthContext';
import TransactionFilters from './TransactionFilters';
import Rows from '../../components/ui/Rows';
import TransactionCreationModal from './TransactionCreationModal';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { transactionAPI, caisseAPI } from '../../services/api';
import { formatAmountForDisplay } from '../../utils/amountFormatter';

const TransactionsContent = ({
  transactions,
  loading,
  error,
  setError,
  filters,
  handleFiltersChange,
  handleExport,
  handleResetFilters,
  hasActiveFilters,
  selectedTransactions,
  setSelectedTransactions,
  pagination,
  setPagination,
  columns,
  transformedTransactions,
  sortBy,
  sortDirection,
  handleSort,
  selectedCaisse,
  setSelectedCaisse,
  caisseOptions
}) => {
  const { user } = useAuth();
  const history = useHistory();
  
  // Caisse dropdown state
  const [isCaisseDropdownOpen, setIsCaisseDropdownOpen] = useState(false);
  
  
  // Selection logic for header checkbox
  const selectableRows = user?.role === 'MANAGER' ? transformedTransactions : [];
  const isAllSelected = user?.role === 'MANAGER' && selectableRows.length > 0 && selectedTransactions.length === selectableRows.length;
  const isIndeterminate = user?.role === 'MANAGER' && selectedTransactions.length > 0 && selectedTransactions.length < selectableRows.length;
  
  const handleSelectAll = () => {
    if (user?.role !== 'MANAGER') return;
    if (isAllSelected) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(selectableRows.map(row => row.id));
    }
  };
  
  // Transaction creation modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    transactionData: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isStuck, setIsStuck] = useState(false);

  const STICKY_TOP_OFFSET = 76;

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const stickyOuterSx = { ...contentContainerSx };

  // Handle clicking outside caisse dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCaisseDropdownOpen) {
        const dropdownElement = event.target.closest('[data-caisse-dropdown]');
        if (!dropdownElement) {
          setIsCaisseDropdownOpen(false);
        }
      }
    };

    if (isCaisseDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCaisseDropdownOpen]);

  // Fetch caisse balance for a specific caisse
  const fetchCaisseBalance = useCallback(async (caisseId) => {
    try {
      const response = await caisseAPI.getForFilters();
      if (response.data && response.data.length > 0) {
        const caisseData = response.data.find(caisse => caisse.id === caisseId);
        const balance = caisseData?.caisseBalance || 0;
        return balance;
      }
    } catch (error) {
      console.error('Error fetching caisse balance:', error);
    }
    return 0;
  }, []);

  const handleCreateTransaction = async (transactionData) => {
    const currentBalance = await fetchCaisseBalance(transactionData.caisseId);
    const category = transactionData.categoryData;
    
    // Validate against caisse balance for outgoing transactions
    if (category && category.caisseFactor === -1) {
      const transactionAmount = transactionData.amount;
      const newBalance = currentBalance - transactionAmount;
      
      if (newBalance < 0) {
        setSnackbar({
          open: true,
          message: `Le montant de cette transaction (${formatAmountForDisplay(transactionAmount, false).text}) dépasse le solde caisse disponible (${formatAmountForDisplay(currentBalance, false).text}). Le solde caisse ne peut pas être négatif.`,
          severity: 'error'
        });
        return;
      }
    }

    setConfirmDialog({
      open: true,
      transactionData: {
        ...transactionData,
        categoryName: transactionData.categoryName || 'Catégorie inconnue'
      }
    });
  };

  // Confirm transaction creation with double-submission protection
  const confirmCreateTransaction = async () => {
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    setIsCreating(true);
    
    try {
      await transactionAPI.create(confirmDialog.transactionData);
      setConfirmDialog({ open: false, transactionData: null });
      setIsCreateModalOpen(false);
      // Refresh the page to show the new transaction
      window.location.reload();
    } catch (error) {
      console.error('Error creating transaction:', error);
      setError('Erreur lors de la création de la transaction');
    } finally {
      setIsSubmitting(false);
      setIsCreating(false);
    }
  };

  // Simple scroll listener for background color change
  useEffect(() => {
    const handleScroll = () => {
      setIsStuck(window.scrollY > STICKY_TOP_OFFSET);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky Header Section */}
      <Box
        className="transactions-sticky-header"
        sx={{
          position: 'sticky',
          top: '0px',
          zIndex: 1000,
          backgroundColor: isStuck ? '#F8F8F8' : 'transparent',
          borderBottom: isStuck ? '1px solid #E5E8EF' : 'none',
          transition: 'background-color 0.2s ease, border-bottom 0.2s ease'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
          {/* Page Title and Create Button */}
          <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '32px',
                letterSpacing: '0px',
                color: '#38485C',
                opacity: 0.88,
                textTransform: 'none',
                margin: 0
              }}
            >
              Transactions
            </h1>
            {user?.role === 'AGENT' && (
              <Button
                variant="contained"
                startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                onClick={() => setIsCreateModalOpen(true)}
                sx={{
                  height: '40px',
                  backgroundColor: '#027BC6',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  opacity: 1,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#025a9c' }
                }}
              >
                Créer une transaction
              </Button>
            )}
          </Box>

          {(user?.role === 'MANAGER' || user?.role === 'VERIFICATION') && selectedCaisse && (
            <Box sx={{ marginBottom: '16px', width: '100%' }}>
              <Box position="relative" data-caisse-dropdown>
                <Box 
                  display="inline-flex" 
                  alignItems="center" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setIsCaisseDropdownOpen(!isCaisseDropdownOpen)}
                >
                  <Typography 
                    style={{
                      fontFamily: 'Montserrat',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '24px',
                      letterSpacing: '0px',
                      color: '#027BC6',
                      textTransform: 'none',
                      margin: 0,
                      marginRight: '8px'
                    }}
                  >
                    {selectedCaisse.label}
                  </Typography>
                  <img 
                    src={chevronDownIcon} 
                    alt="Chevron Down" 
                    className={`h-3 w-3 transform transition-transform duration-200 ${
                      isCaisseDropdownOpen ? 'rotate-180' : ''
                    }`}
                    style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)' }}
                  />
                </Box>
                {isCaisseDropdownOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    mt={1}
                    bgcolor="white"
                    border="1px solid #e5e7eb"
                    borderRadius="8px"
                    boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                    zIndex={1200}
                    minWidth="200px"
                  >
                    {caisseOptions.map((option) => (
                      <Box
                        key={option.value}
                        px={2}
                        py={1.5}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedCaisse?.value === option.value ? '#f0f9ff' : 'transparent',
                          color: selectedCaisse?.value === option.value ? '#027BC6' : '#38485C',
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: selectedCaisse?.value === option.value ? 700 : 500
                        }}
                        onMouseEnter={(e) => {
                          if (selectedCaisse?.value !== option.value) {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedCaisse?.value !== option.value) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={() => {
                          setSelectedCaisse(option);
                          setIsCaisseDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
            <TransactionFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onExport={handleExport}
              onReset={handleResetFilters}
              userRole={user?.role}
              user={user}
              hasActiveFilters={hasActiveFilters()}
              hasSelection={selectedTransactions.length > 0}
              transactions={transactions}
            />
          </Box>
          </Box>
        </Box>

        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat'
            }}
          >
            {user?.role === 'MANAGER' && (
              <Box sx={{ flex: 'none', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '1px solid #C5C5C5',
                    borderRadius: '4px',
                    opacity: 1,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundColor: isAllSelected || isIndeterminate ? '#007BC6' : 'white',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isAllSelected && !isIndeterminate) {
                      e.target.style.borderColor = '#007BC6';
                      e.target.style.backgroundColor = '#F0F8FF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isAllSelected && !isIndeterminate) {
                      e.target.style.borderColor = '#C5C5C5';
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                />
                {isAllSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '14px',
                      height: '14px',
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={checkIcon}
                      alt="check"
                      style={{
                        width: '10px',
                        height: '10px',
                        filter: 'brightness(0) invert(1) drop-shadow(0 0 0.5px white)'
                      }}
                    />
                  </Box>
                )}
                {isIndeterminate && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '8px',
                      height: '2px',
                      backgroundColor: 'white',
                      borderRadius: '1px',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </Box>
            )}
            
              {columns.map((column, index) => {
              const isActionsColumn = column.key === 'actions';
              const isSortable = column.sortable && handleSort;
              const isCurrentlySorted = sortBy === column.key;
              
              return (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    textAlign: isActionsColumn ? 'center' : 'left',
                    ...(isActionsColumn 
                      ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                      : column.key === 'description'
                        ? {}
                        : column.key === 'amount' || column.key === 'montant'
                          ? { flex: 'none', width: '200px' }
                          : column.key === 'status' || column.key === 'statut'
                            ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                            : column.width
                              ? { flex: 'none', width: column.width }
                              : { flex: 1, minWidth: '150px' }),
                    ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                    ...(column.width && !isActionsColumn ? { width: column.width } : {})
                  }}
                >
                  {isSortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                        color: isCurrentlySorted ? '#027BC6' : '#919191'
                      }}
                      onClick={() => handleSort(column.key, sortBy === column.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                    >
                      <span>{column.label}</span>
                      {isCurrentlySorted && (
                        <img
                          src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                          alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                          style={{
                            width: '12px',
                            height: '12px',
                            filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    column.label
                  )}
                </Box>
              );
              })}
          </Box>
        </Box>
      </Box>

      <Box sx={contentContainerSx}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Transaction List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Rows
            columns={columns}
            data={transformedTransactions}
            onRowClick={(row) => {
              history.push(`/transactions/${row.id}`);
            }}
            selectable={user?.role === 'MANAGER'}
            selectedRows={selectedTransactions}
            onSelectionChange={setSelectedTransactions}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onPageChange: (newPage) => setPagination(prev => ({ ...prev, page: newPage })),
              onSizeChange: (newSize) => setPagination(prev => ({ ...prev, size: newSize, page: 0 }))
            }}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            hideHeader={true}
            containerPadding={0}
            emptyMessage={hasActiveFilters() ? 'Aucune transaction trouvée avec ces critères' : 'Aucune transaction trouvée'}
          />
        )}
      </Box>

      {/* Transaction Creation Modal */}
      <TransactionCreationModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateTransaction}
        loading={isCreating}
      />
      
      {/* Transaction Creation Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, transactionData: null })}
        onConfirm={confirmCreateTransaction}
        title="Confirmer la création de transaction"
        message={
          <Box>
            <Typography sx={{ fontSize: '18px', color: '#374151', textAlign: 'center', fontFamily: 'Montserrat', fontWeight: 500 }}>
              Êtes-vous sûr de vouloir créer une transaction de type{' '}
              <span style={{ color: '#007BC6', fontWeight: '600' }}>{confirmDialog.transactionData?.categoryName}</span>
              {' '}avec un montant de{' '}
              <span style={{ color: '#007BC6', fontWeight: '600' }}>
                {confirmDialog.transactionData?.amount ? 
                  formatAmountForDisplay(confirmDialog.transactionData.amount, false).text : 
                  'N/A'
                }
              </span>
              ?
            </Typography>
          </Box>
        }
        confirmText="Créer la transaction"
        cancelText="Annuler"
        loading={isSubmitting}
        confirmButtonSx={{ minWidth: '250px' }}
      />

      {/* Snackbar for validation errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionsContent;
